import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Award,
  ChevronDown,
  Gift,
  Globe,
  Loader2,
  MapPin,
  MessageCircle,
  Megaphone,
  Shield,
  Smartphone,
  Star,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import SEO from "../components/seo/SEO";
import UniversitySelect from "../components/ui/UniversitySelect";
import { useUniversities } from "../hooks/useUniversities";
import { APP_STORE_URL, PLAY_STORE_URL, trackStoreClick } from "../constants/appStores";

import { API_BASE_URL } from "../config/api";

// ─── Data ─────────────────────────────────────────────────────────────────────

const STATS = [
  { value: "₦500", label: "Per verified referral" },
  { value: "Free", label: "Pro plan for life" },
  { value: "20+", label: "Target campuses" },
  { value: "Top 3", label: "Reps win monthly bonus" },
];

const BENEFITS = [
  {
    icon: Smartphone,
    title: "Free Pro Plan",
    desc: "Get full CampusGenie Pro access with AI tools, GPA tracker, career features, everything at zero cost, for as long as you're a rep.",
  },
  {
    icon: TrendingUp,
    title: "₦500 Per Referral",
    desc: "Every student you convert to a paid plan puts ₦500 in your pocket. No cap. The more you activate, the more you earn.",
  },
  {
    icon: Gift,
    title: "Branded Merch Kit",
    desc: "Rep pack with CampusGenie merch, stickers, and campaign materials to make your activations stand out.",
  },
  {
    icon: Award,
    title: "Monthly Leaderboard Bonuses",
    desc: "Top 3 reps every month win cash bonuses on top of their referral earnings. Compete, climb, cash out.",
  },
  {
    icon: Star,
    title: "Early Feature Access",
    desc: "You're the first to try new features before they ship to everyone. Your feedback directly shapes the product.",
  },
  {
    icon: Globe,
    title: "Alumni Network Access",
    desc: "When you graduate, your ambassador status follows you, unlocking our alumni network and career features.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Apply",
    desc: "Fill in the form below. Tell us about yourself, your campus network, and why you want to rep CampusGenie. We review every application personally.",
  },
  {
    num: "02",
    title: "Get Equipped",
    desc: "Accepted reps get onboarded via a private WhatsApp group, receive their Pro plan activation, merch kit, and a personalised referral link.",
  },
  {
    num: "03",
    title: "Activate Your Campus",
    desc: "Host study sessions, share CampusGenie in departmental groups, post content, and get classmates signed up using your link.",
  },
  {
    num: "04",
    title: "Earn",
    desc: "Every paid conversion through your link triggers ₦500 to your wallet. Monthly leaderboard winners get additional cash bonuses.",
  },
];

const REQUIREMENTS = [
  "Currently enrolled at a Nigerian university or polytechnic (100L–400L or HND 1–4)",
  "Active in your department as a course rep, class gov't member, or known social hub",
  "Member of at least one departmental WhatsApp group with 50+ members",
  "Genuine passion for education technology and helping fellow students",
  "Able to commit 3–5 hours per week to campus activations and referrals",
  "Own an Android or iOS smartphone",
];

const CAMPUSES = [
  { name: "University of Lagos", short: "UNILAG", city: "Lagos" },
  { name: "Obafemi Awolowo University", short: "OAU", city: "Ile-Ife" },
  { name: "University of Ibadan", short: "UI", city: "Ibadan" },
  { name: "University of Nigeria", short: "UNN", city: "Nsukka" },
  { name: "University of Benin", short: "UNIBEN", city: "Benin City" },
  { name: "Covenant University", short: "CU", city: "Ota" },
  { name: "Ahmadu Bello University", short: "ABU", city: "Zaria" },
  { name: "Lagos State University", short: "LASU", city: "Lagos" },
];

const FAQS = [
  {
    q: "Is this a paid role?",
    a: "It's not a salary, it's a performance-based earnings model. You earn ₦500 per student you convert to a paid CampusGenie plan. Top reps earning consistently can make ₦10,000–₦50,000+ per month depending on their campus network size and activation effort.",
  },
  {
    q: "What does 'verified referral' mean?",
    a: "A verified referral is when a student signs up via your unique referral link AND activates a paid plan (Scholar or Pro). Free signups alone don't count; the student needs to convert to paid. We show you real-time conversions in your dashboard.",
  },
  {
    q: "How many reps do you take per campus?",
    a: "We take 1–3 reps per faculty or department, not per campus. So there's room for multiple reps from the same university as long as you're in different departments or faculties.",
  },
  {
    q: "Do I need to be a course rep?",
    a: "No. Being an official course rep is a plus, but not a requirement. We care more about your reach. If you're the person everyone in your department knows and trusts, you're the right fit.",
  },
  {
    q: "How long does the program run?",
    a: "The program runs indefinitely. You stay a rep for as long as you're active, making at least 2 conversions per month. Inactive reps are removed after 60 days of zero activity so we keep the network high-quality.",
  },
  {
    q: "When and how do I get paid?",
    a: "Earnings are calculated weekly and paid out on the first Friday of every month via bank transfer to your registered account. Minimum payout is ₦2,000.",
  },
];

const LEVELS = ["100 Level", "200 Level", "300 Level", "400 Level", "500 Level", "HND 1", "HND 2", "Postgraduate"];
const WHATSAPP_SIZES = ["Under 100 members", "100–250 members", "250–500 members", "500+ members", "I manage multiple groups"];

// ─── University Dropdown ───────────────────────────────────────────────────────

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--color-border)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", padding: "var(--space-5) 0", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
        aria-expanded={open}
      >
        <span style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-semibold)", color: "var(--color-text-primary)" }}>{q}</span>
        <ChevronDown size={18} style={{ color: "var(--color-primary)", flexShrink: 0, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="faq-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
          >
            <p style={{ paddingBottom: "var(--space-5)", color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", lineHeight: 1.7, margin: 0 }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Form Field Wrapper ───────────────────────────────────────────────────────

function Field({ id, label, error, optional, hint, children }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label}{" "}
        {optional
          ? <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span>
          : <span style={{ color: "var(--color-primary)" }}>*</span>}
      </label>
      {children}
      {hint && !error && <p style={{ color: "var(--color-text-tertiary)", fontSize: "var(--text-sm)", marginTop: 6 }}>{hint}</p>}
      {error && <p style={{ color: "#ef4444", fontSize: "var(--text-sm)", marginTop: 6 }}>{error}</p>}
    </div>
  );
}

// ─── Application Form ─────────────────────────────────────────────────────────

function ApplicationForm({ universities, uniLoading }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", username: "", phone: "",
    university: "", department: "", level: "",
    whatsappSize: "", instagram: "", tiktok: "", why: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setDirect = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.username.trim().replace(/^@/, "")) e.username = "Enter your CampusGenie username";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.university) e.university = "Select your university";
    if (!form.department.trim()) e.department = "Required";
    if (!form.level) e.level = "Select your level";
    if (!form.whatsappSize) e.whatsappSize = "Required";
    if (!form.why.trim() || form.why.trim().length < 80) e.why = "Tell us more (at least 80 characters)";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setErrorMsg("");

    if (!API_BASE_URL) {
      setErrorMsg("Service is unavailable right now. Email us at hello@campusgenie.app to apply.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    try {
      const body = JSON.stringify({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        campusgenie_username: form.username.trim().replace(/^@/, ""),
        phone_number: form.phone.trim(),
        university: form.university,
        department: form.department.trim(),
        level: form.level,
        whatsapp_group_size: form.whatsappSize,
        instagram_handle: form.instagram.trim() || null,
        tiktok_handle: form.tiktok.trim() || null,
        why_ambassador: form.why.trim(),
      });
      const res = await fetch(`${API_BASE_URL}/ambassador/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Submission failed");
      setStatus("success");
    } catch (err) {
      setErrorMsg(err.message || "Couldn't submit your application. Please try again.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ textAlign: "center", padding: "var(--space-16) var(--space-8)" }}
      >
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--color-primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto var(--space-6)" }}>
          <Zap size={34} style={{ color: "var(--color-primary)" }} />
        </div>
        <h3 className="heading-3" style={{ marginBottom: "var(--space-3)" }}>Application received! 🎉</h3>
        <p style={{ color: "var(--color-text-secondary)", maxWidth: 480, margin: "0 auto var(--space-2)" }}>
          Thanks for applying. We review every ambassador application personally and will be in touch within <strong>3–5 business days</strong>.
        </p>
        <p style={{ color: "var(--color-text-tertiary)", fontSize: "var(--text-sm)" }}>
          Keep an eye on your inbox, including your spam folder.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="ambassador-form">
      {status === "error" && (
        <div style={{ padding: "12px 16px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "var(--radius-lg)", color: "#ef4444", fontSize: "var(--text-sm)", marginBottom: "var(--space-5)" }}>
          {errorMsg}
        </div>
      )}

      {/* Name */}
      <div className="ambassador-form-row">
        <Field id="firstName" label="First Name" error={errors.firstName}>
          <input id="firstName" type="text" className={`form-input${errors.firstName ? " input-error" : ""}`} placeholder="Ada" value={form.firstName} onChange={set("firstName")} autoComplete="given-name" />
        </Field>
        <Field id="lastName" label="Last Name" error={errors.lastName}>
          <input id="lastName" type="text" className={`form-input${errors.lastName ? " input-error" : ""}`} placeholder="Okafor" value={form.lastName} onChange={set("lastName")} autoComplete="family-name" />
        </Field>
      </div>

      <Field id="email" label="Email Address" error={errors.email}>
        <input id="email" type="email" className={`form-input${errors.email ? " input-error" : ""}`} placeholder="you@email.com" value={form.email} onChange={set("email")} autoComplete="email" />
      </Field>

      <Field
        id="username"
        label="CampusGenie Username"
        error={errors.username}
        hint="Your username in the CampusGenie app. Create your free account in the app first: we use this to find your account and activate your free Pro plan if you're accepted."
      >
        <input id="username" type="text" className={`form-input${errors.username ? " input-error" : ""}`} placeholder="e.g. adaokafor" value={form.username} onChange={set("username")} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      </Field>

      <Field id="phone" label="Phone Number" error={errors.phone}>
        <input id="phone" type="tel" className={`form-input${errors.phone ? " input-error" : ""}`} placeholder="08012345678" value={form.phone} onChange={set("phone")} autoComplete="tel" />
      </Field>

      <Field id="university" label="University / Polytechnic" error={errors.university}>
        <UniversitySelect
                  universities={universities}
                  loading={uniLoading}
                  value={form.university}
                  onChange={setDirect("university")}
                  error={errors.university}
                />
      </Field>

      <div className="ambassador-form-row">
        <Field id="department" label="Department / Faculty" error={errors.department}>
          <input id="department" type="text" className={`form-input${errors.department ? " input-error" : ""}`} placeholder="e.g. Computer Science" value={form.department} onChange={set("department")} />
        </Field>
        <Field id="level" label="Current Level" error={errors.level}>
          <select id="level" className={`form-input${errors.level ? " input-error" : ""}`} value={form.level} onChange={set("level")} style={{ cursor: "pointer" }}>
            <option value="">Select level</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
      </div>

      <Field id="whatsappSize" label="Your largest departmental WhatsApp group" error={errors.whatsappSize}>
        <select id="whatsappSize" className={`form-input${errors.whatsappSize ? " input-error" : ""}`} value={form.whatsappSize} onChange={set("whatsappSize")} style={{ cursor: "pointer" }}>
          <option value="">Select approximate size</option>
          {WHATSAPP_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>

      <div className="ambassador-form-row">
        <Field id="instagram" label="Instagram Handle" error={errors.instagram} optional>
          <input id="instagram" type="text" className="form-input" placeholder="@yourhandle" value={form.instagram} onChange={set("instagram")} />
        </Field>
        <Field id="tiktok" label="TikTok Handle" error={errors.tiktok} optional>
          <input id="tiktok" type="text" className="form-input" placeholder="@yourhandle" value={form.tiktok} onChange={set("tiktok")} />
        </Field>
      </div>

      <Field id="why" label="Why do you want to be a CampusGenie rep?" error={errors.why}>
        <textarea
          id="why"
          className={`form-textarea${errors.why ? " input-error" : ""}`}
          placeholder="Tell us about your campus network, how you'd activate students, and what drives you. Be specific. Generic answers don't stand out."
          value={form.why}
          onChange={set("why")}
          rows={5}
          style={{ resize: "vertical", minHeight: 120 }}
        />
        <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" }}>
          {form.why.length} chars {form.why.length < 80 ? `· ${80 - form.why.length} more to go` : "· ✓"}
        </span>
      </Field>

      <button
        type="submit"
        className="btn btn-primary btn-lg"
        disabled={status === "loading"}
        style={{ width: "100%", justifyContent: "center", marginTop: "var(--space-2)" }}
      >
        {status === "loading"
          ? <><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</>
          : "Submit Application →"}
      </button>

      <p style={{ textAlign: "center", fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "var(--space-3)" }}>
        We review every application personally and respond within 3–5 business days.
      </p>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AmbassadorPage() {
  const { universities, loading: uniLoading } = useUniversities();

  return (
    <div className="ambassador-page">
      <SEO
        title="Campus Ambassador Program · CampusGenie"
        description="Represent CampusGenie on your campus. Earn ₦500 per referral, get a free Pro plan, and be the student every classmate thanks for putting them onto the best study app in Nigeria."
        url="/ambassador"
      />

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="hero ambassador-hero">
        <div className="container">
          <Reveal>
            <div className="ambassador-hero-inner">
              <div className="hero-badge" style={{ display: "inline-flex", marginBottom: "var(--space-5)" }}>
                <span className="hero-badge-dot" />
                Now recruiting across 20 campuses
              </div>
              <h1 className="heading-hero hero-title" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
                Represent CampusGenie{" "}
                <span className="text-gradient">on your campus.</span>
              </h1>
              <p className="body-large" style={{ color: "var(--color-text-secondary)", marginTop: "var(--space-4)", maxWidth: 560 }}>
                Join our campus ambassador program. Earn ₦500 per paid referral, get a free Pro plan, and help your classmates unlock the app that makes university life less stressful.
              </p>
              <div className="ambassador-hero-actions">
                <a href="#apply" className="btn btn-primary btn-lg">
                  Apply to be a Rep →
                </a>
                <a href="#how-it-works" className="btn btn-secondary btn-lg">
                  How it works
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Stats Strip ─────────────────────────────────────────────────────── */}
      <section className="ambassador-stats">
        <div className="container">
          <div className="ambassador-stats-grid">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div>
                  <div className="ambassador-stat-value">{s.value}</div>
                  <div className="ambassador-stat-label">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── What is it ──────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="ambassador-split">
            <Reveal>
              <div>
                <span className="eyebrow">The program</span>
                <h2 className="heading-2" style={{ marginTop: "var(--space-3)", marginBottom: "var(--space-5)" }}>
                  Be the person who puts your campus on.
                </h2>
                <p style={{ color: "var(--color-text-secondary)", lineHeight: 1.75, marginBottom: "var(--space-5)" }}>
                  CampusGenie works best when students hear about it from someone they trust  a classmate, a course rep, someone in their WhatsApp group. That's you.
                </p>
                <p style={{ color: "var(--color-text-secondary)", lineHeight: 1.75 }}>
                  As a campus rep, you get your own referral link, a free Pro plan, and real cash for every student you convert. We'll equip you with everything you need to run activations  you just need to show up and know your campus.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="ambassador-block-stack">
                {[
                  { icon: Megaphone, title: "You do", items: ["Share in departmental WhatsApp groups", "Host study sessions and activations", "Post content about CampusGenie", "Convert classmates to paid plans"] },
                  { icon: Gift, title: "You get", items: ["Free Pro plan with all features unlocked", "₦500 per verified paid referral", "Monthly leaderboard cash bonuses", "Branded merch + early product access"] },
                ].map((block) => {
                  const Icon = block.icon;
                  return (
                    <div key={block.title} className="ambassador-info-card">
                      <div className="ambassador-info-card-head">
                        <div className="ambassador-info-icon">
                          <Icon size={18} style={{ color: "var(--color-primary)" }} />
                        </div>
                        <span style={{ fontWeight: "var(--font-semibold)", fontSize: "var(--text-base)" }}>{block.title}</span>
                      </div>
                      <ul className="ambassador-info-list">
                        {block.items.map((item) => (
                          <li key={item}>
                            <span>●</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Benefits Grid ───────────────────────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center" style={{ marginBottom: "var(--space-12)" }}>
              <span className="eyebrow">What you get</span>
              <h2 className="heading-2">Real perks for real reps.</h2>
              <p className="body-large" style={{ color: "var(--color-text-secondary)" }}>
                No vague "exposure." Actual things that make being a rep worth it.
              </p>
            </div>
          </Reveal>
          <div className="ambassador-benefits-grid">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div className="ambassador-benefit-card">
                    <div className="ambassador-benefit-icon">
                      <Icon size={22} style={{ color: "var(--color-primary)" }} />
                    </div>
                    <h4 style={{ fontWeight: "var(--font-semibold)", marginBottom: "var(--space-2)" }}>{b.title}</h4>
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.65 }}>{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center" style={{ marginBottom: "var(--space-12)" }}>
              <span className="eyebrow">The process</span>
              <h2 className="heading-2">From application to earnings.</h2>
            </div>
          </Reveal>
          <div className="ambassador-steps-grid">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.1}>
                <div>
                  <div className="ambassador-step-num">{step.num}</div>
                  <h4 style={{ fontWeight: "var(--font-semibold)", fontSize: "var(--text-lg)", marginBottom: "var(--space-3)" }}>{step.title}</h4>
                  <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Requirements ────────────────────────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <div className="ambassador-split ambassador-split-start">
            <Reveal>
              <div>
                <span className="eyebrow">Who we're looking for</span>
                <h2 className="heading-2" style={{ marginTop: "var(--space-3)", marginBottom: "var(--space-5)" }}>
                  You don't need a title. You need a network.
                </h2>
                <p style={{ color: "var(--color-text-secondary)", lineHeight: 1.75 }}>
                  We're not looking for course reps only. We're looking for students people actually listen to. If your classmates ask you for recommendations and trust your word, you're already qualified.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="ambassador-block-stack">
                {REQUIREMENTS.map((req, i) => (
                  <div key={i} className="ambassador-requirement">
                    <div className="ambassador-requirement-icon">
                      <Shield size={13} style={{ color: "var(--color-primary)" }} />
                    </div>
                    <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: 1.6 }}>{req}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Target Campuses ─────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center" style={{ marginBottom: "var(--space-10)" }}>
              <span className="eyebrow">Wave 1 campuses</span>
              <h2 className="heading-2">Where we're starting.</h2>
              <p className="body-large" style={{ color: "var(--color-text-secondary)", maxWidth: 500, margin: "var(--space-4) auto 0" }}>
                These are our priority campuses for the first cohort. Not seeing yours? Apply anyway. We expand based on rep quality, not location.
              </p>
            </div>
          </Reveal>
          <div className="ambassador-campuses-grid">
            {CAMPUSES.map((c, i) => (
              <Reveal key={c.short} delay={i * 0.06}>
                <div className="ambassador-campus-card">
                  <div className="ambassador-campus-badge">{c.short}</div>
                  <div>
                    <div style={{ fontWeight: "var(--font-semibold)", fontSize: "var(--text-sm)", lineHeight: 1.3 }}>{c.name}</div>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <MapPin size={10} /> {c.city}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social Proof Banner ─────────────────────────────────────────────── */}
      <section className="ambassador-proof">
        <div className="container">
          <Reveal>
            <div className="ambassador-proof-inner">
              <div className="ambassador-proof-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} fill="#fff" style={{ color: "#fff", opacity: 0.9 }} />
                ))}
              </div>
              <h2 className="ambassador-proof-quote">
                "The app I wish I had in 100 level."
              </h2>
              <p className="ambassador-proof-text">
                Join 1,000+ students already using CampusGenie. As a rep, you'll be the reason your campus gets access to Nigeria's best student OS.
              </p>
              <div className="ambassador-proof-chips">
                {[
                  { icon: Users, text: "1,000+ active students" },
                  { icon: MessageCircle, text: "Active campus communities" },
                  { icon: Zap, text: "AI-powered study tools" },
                ].map(({ icon: Icon, text }, i, arr) => (
                  <React.Fragment key={text}>
                    <div className="ambassador-proof-chip">
                      <Icon size={14} style={{ color: "rgba(255,255,255,0.85)", flexShrink: 0 }} />
                      <span>{text}</span>
                    </div>
                    {i < arr.length - 1 && (
                      <span className="ambassador-proof-divider">·</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container" style={{ maxWidth: 720 }}>
          <Reveal>
            <div className="section-heading section-heading-center" style={{ marginBottom: "var(--space-10)" }}>
              <span className="eyebrow">Before you apply</span>
              <h2 className="heading-2">Common questions.</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ borderTop: "1px solid var(--color-border)" }}>
              {FAQS.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Application Form ────────────────────────────────────────────────── */}
      <section id="apply" className="section section-alt">
        <div className="container ambassador-form-wrap">
          <Reveal>
            <div className="ambassador-form-intro">
              <span className="eyebrow">Apply now</span>
              <h2 className="heading-2" style={{ marginTop: "var(--space-3)" }}>
                Ready to rep your campus?
              </h2>
              <p style={{ color: "var(--color-text-secondary)", marginTop: "var(--space-4)", maxWidth: 480, margin: "var(--space-4) auto 0" }}>
                Fill in the form. We review every application personally and get back to accepted reps within 3–5 business days.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="ambassador-form-card">
              <ApplicationForm universities={universities} uniLoading={uniLoading} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Bottom CTA ──────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="ambassador-bottom-cta">
              <h3 className="heading-3" style={{ marginBottom: "var(--space-3)" }}>
                Not ready to rep? Start as a student.
              </h3>
              <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--space-6)", maxWidth: 420, margin: "0 auto var(--space-6)" }}>
                Download CampusGenie and try it yourself first. Great reps are students who genuinely love the product.
              </p>
              <div className="ambassador-bottom-actions">
                <a href={APP_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Download on iOS
                </a>
                <a href={PLAY_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                  Get it on Android
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
